import React, { useCallback, useEffect, useId, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { ABSENT, formatTimestamp, toIsoInstant } from '../../../shared/datetime/timestamps';
import { useNow } from './useNow';
import './Timestamp.css';

/**
 * The one way a screen shows a stored time. The words come from the shared formatter;
 * this adds what a screen needs around them.
 *
 *   <Timestamp value={row.updated_at} />                 // short: lists, cards, cells
 *   <Timestamp value={doc.created_at} form="long" />     // long: pages, panels, chat
 *   <Timestamp value={entry.at} form="static" />         // static: audit logs, exports
 *
 * A short or long time ("6h ago", "Today, 9:42 AM") reveals the full time with its zone
 * ("14 March 2026, 9:42 AM ADT") on hover and on keyboard focus: neither names the zone,
 * and both go relative. Only the static form, which is already the full time, does not.
 * A `title` attribute alone is neither reachable by keyboard nor reliably announced, so:
 *   - the <time> itself takes focus, and its name is only the short text;
 *   - screen readers get the full time as its description, from a hidden element
 *     beside it, whether or not the reveal is showing;
 *   - the visible reveal is drawn at the page root, so a scrolling list cannot clip
 *     it, above the time or below it when there is no room. It stays while the
 *     pointer moves onto it, and while the time keeps keyboard focus, and Escape
 *     hides it wherever focus is. A press on the time closes it, and nothing
 *     reopens it until the button is released: a press means a click or a drag,
 *     not "show me", and the reveal would otherwise stay behind over whatever the
 *     click opens, or where a dragged card used to be.
 *
 * Relative forms redraw on the shared clock, so a page left open stays true. On paper
 * a short or long time prints as the full time with zone instead: a printout is read
 * after the moment it describes, which is what the static form is for. Every form
 * carries the instant in <time datetime> for anything reading the page.
 * A missing or unreadable time renders the formatter's absence value and nothing else.
 */
export default function Timestamp({ value, form = 'short', className = '' }) {
  useNow(form !== 'static');

  const text = formatTimestamp(value, form);
  const classes = ['resui-timestamp', className].filter(Boolean).join(' ');
  if (text === ABSENT) return <span className={classes}>{ABSENT}</span>;
  if (form === 'static') {
    return (
      <span className={classes}>
        <time dateTime={toIsoInstant(value)}>{text}</time>
      </span>
    );
  }
  return <RevealingTimestamp value={value} text={text} className={classes} />;
}

// How long the reveal waits after the pointer leaves before closing, so the pointer
// can cross the gap onto it.
const CLOSE_DELAY_MS = 120;
// Space kept between the reveal and the time, and between the reveal and the window
// edge: --gap-1.
const GAP_PX = 4;
// What ends a press, taken on the document in the capture phase so a release outside the
// time, or one a drag library stops, still counts: the button coming up, the browser
// cancelling the pointer, or a key (a keyboard user is not pressing). The window losing
// focus ends it too. A release outside the window reaches none of these, so the pointer
// coming back over the time with no button down ends it as well (pointerIn).
const ENDS_A_PRESS = ['mouseup', 'pointercancel', 'keydown'];

function RevealingTimestamp({ value, text, className }) {
  const [open, setOpen] = useState(false);
  const [position, setPosition] = useState(null);
  const descriptionId = useId();
  const timeRef = useRef(null);
  const tipRef = useRef(null);
  const closeTimer = useRef(null);
  const full = formatTimestamp(value, 'static');

  // What is holding the reveal open. Each closes it only for itself: a reveal shown on focus
  // stays while the time keeps focus, however the pointer passes over it.
  const hovered = useRef(false);
  const focused = useRef(false);
  // From a mouse press on the time until the button is released, anywhere on the page.
  // Meanwhile neither the focus the click gives the time nor the pointer re-entering it
  // (a dragged card moves under the pointer) reopens the reveal.
  const held = useRef(false);

  const show = useCallback(() => {
    clearTimeout(closeTimer.current);
    setOpen(true);
  }, []);
  const hideNow = useCallback(() => {
    clearTimeout(closeTimer.current);
    setOpen(false);
  }, []);
  const release = useCallback(() => {
    held.current = false;
    ENDS_A_PRESS.forEach((type) => document.removeEventListener(type, release, true));
    window.removeEventListener('blur', release);
  }, []);
  const pointerIn = useCallback((e) => {
    hovered.current = true;
    if (held.current && e && e.buttons === 0) release();
    if (!held.current) show();
  }, [show, release]);
  const pointerOut = useCallback(() => {
    hovered.current = false;
    if (focused.current) return;
    clearTimeout(closeTimer.current);
    closeTimer.current = setTimeout(() => setOpen(false), CLOSE_DELAY_MS);
  }, []);
  const focusIn = useCallback(() => {
    if (held.current) return;
    focused.current = true;
    show();
  }, [show]);
  const press = useCallback(() => {
    held.current = true;
    focused.current = false;
    hideNow();
    ENDS_A_PRESS.forEach((type) => document.addEventListener(type, release, true));
    window.addEventListener('blur', release);
  }, [hideNow, release]);
  useEffect(() => release, [release]);
  const focusOut = useCallback(() => {
    focused.current = false;
    if (!hovered.current) hideNow();
  }, [hideNow]);
  useEffect(() => () => clearTimeout(closeTimer.current), []);

  // Escape dismisses it wherever focus is, so a reveal opened by hovering can be closed
  // without moving the pointer (WCAG 1.4.13): a magnifier's view follows the pointer, so
  // moving away also moves what is being read. Taken in the capture phase and stopped, so
  // the first Escape closes this rather than, say, a dialog the time sits in.
  useEffect(() => {
    if (!open) return undefined;
    const onKeyDown = (e) => {
      if (e.key !== 'Escape') return;
      e.stopPropagation();
      hideNow();
    };
    document.addEventListener('keydown', onKeyDown, true);
    return () => document.removeEventListener('keydown', onKeyDown, true);
  }, [open, hideNow]);

  // Placed from the time's on-screen box once the reveal has a size: above and
  // centred, below when there is no room above, kept inside the window.
  const place = useCallback(() => {
    const anchor = timeRef.current;
    const tip = tipRef.current;
    if (!anchor || !tip) return;
    const a = anchor.getBoundingClientRect();
    const t = tip.getBoundingClientRect();
    const above = a.top - t.height - GAP_PX;
    const top = above >= GAP_PX ? above : a.bottom + GAP_PX;
    const centred = a.left + a.width / 2 - t.width / 2;
    const left = Math.min(Math.max(centred, GAP_PX), window.innerWidth - t.width - GAP_PX);
    setPosition({ top, left, below: above < GAP_PX });
  }, []);

  useLayoutEffect(() => {
    if (!open) {
      setPosition(null);
      return undefined;
    }
    place();
    window.addEventListener('scroll', place, true);
    window.addEventListener('resize', place);
    return () => {
      window.removeEventListener('scroll', place, true);
      window.removeEventListener('resize', place);
    };
  }, [open, place]);

  return (
    <span className={`${className} resui-timestamp--reveal`}>
      <time
        ref={timeRef}
        dateTime={toIsoInstant(value)}
        // eslint-disable-next-line jsx-a11y/no-noninteractive-tabindex
        tabIndex={0}
        // The time role takes no name from its text, so a focused time would be
        // announced with no name at all.
        aria-label={text}
        aria-describedby={descriptionId}
        onMouseEnter={pointerIn}
        onMouseLeave={pointerOut}
        onMouseDown={press}
        onFocus={focusIn}
        onBlur={focusOut}
      >
        {text}
      </time>
      <span id={descriptionId} hidden>{full}</span>
      {/* Print only (Timestamp.css). Hidden from assistive technology, which already has
          the full time as the description. */}
      <span className="resui-timestamp__print" aria-hidden="true">{full}</span>
      {open && createPortal(
        // Visual only: the description above is what assistive technology reads.
        <span
          ref={tipRef}
          className={`resui-timestamp__tip${position && position.below ? ' resui-timestamp__tip--below' : ''}`}
          aria-hidden="true"
          style={position ? { top: position.top, left: position.left } : { visibility: 'hidden', top: 0, left: 0 }}
          onMouseEnter={pointerIn}
          onMouseLeave={pointerOut}
        >
          {full}
        </span>,
        document.body,
      )}
    </span>
  );
}
